import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const GOLD = [197, 160, 89];
const SLATE = [15, 23, 42];
const MUTED = [100, 116, 139];

const eur = (n) =>
  new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(n || 0))
    .replace(/[\u202f\u00a0]/g, " ") + " €";

const frDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export function buildDocumentPDF(doc, settings, type = "invoice") {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const M = 16;
  const isInvoice = type === "invoice";
  const title = isInvoice ? "FACTURE" : "DEVIS";

  // ---- Header band ----
  pdf.setFillColor(...SLATE);
  pdf.rect(0, 0, W, 34, "F");
  pdf.setFillColor(...GOLD);
  pdf.rect(0, 34, W, 1.5, "F");

  pdf.setTextColor(...GOLD);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.text(settings.company_name || "MB TRANSPORT", M, 16);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(settings.subtitle || "Transport de personnes — VTC", M, 22);
  pdf.setFontSize(8);
  pdf.text(
    `${settings.address || ""} · ${settings.postal_city || ""}`,
    M,
    27
  );
  pdf.text(`SIRET ${settings.siret || ""}  ·  TVA ${settings.tva_number || ""}`, M, 31);

  // Document title on the right
  pdf.setTextColor(...GOLD);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.text(title, W - M, 18, { align: "right" });
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(`N° ${doc.number || ""}`, W - M, 26, { align: "right" });

  // ---- Meta + client ----
  let y = 48;
  pdf.setTextColor(...SLATE);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.text("FACTURÉ À", M, y);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  const c = doc.client || {};
  let cy = y + 6;
  pdf.setFont("helvetica", "bold");
  pdf.text(c.name || "—", M, cy);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(...MUTED);
  if (c.address) { cy += 5; pdf.text(c.address, M, cy); }
  if (c.postal_city) { cy += 5; pdf.text(c.postal_city, M, cy); }
  if (c.siret) { cy += 5; pdf.text(`SIRET : ${c.siret}`, M, cy); }
  if (c.tva_number) { cy += 5; pdf.text(`TVA : ${c.tva_number}`, M, cy); }

  // meta box (right)
  pdf.setTextColor(...SLATE);
  const bx = 130;
  const rows = [
    ["Date d'émission", frDate(doc.date_emission)],
    [isInvoice ? "Échéance" : "Valide jusqu'au", frDate(doc.date_echeance)],
  ];
  let my = y;
  rows.forEach(([k, v]) => {
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...MUTED);
    pdf.setFontSize(9);
    pdf.text(k, bx, my);
    pdf.setTextColor(...SLATE);
    pdf.setFont("helvetica", "bold");
    pdf.text(v || "—", W - M, my, { align: "right" });
    my += 7;
  });

  // ---- Line items table ----
  const body = (doc.line_items || []).map((li) => {
    const parts = [];
    if (li.description) parts.push(li.description);
    if (li.depart) parts.push(`Départ : ${li.depart}`);
    if (li.arrivee) parts.push(`Arrivée : ${li.arrivee}`);
    const meta = [];
    if (li.date_course) meta.push(li.date_course);
    if (meta.length) parts.push(meta.join("   ·   "));
    const desc = parts.join("\n");
    const lht = Number(li.quantity || 1) * Number(li.prix_unitaire_ht || 0);
    return [
      desc,
      String(li.quantity || 1),
      eur(li.prix_unitaire_ht),
      `${li.taux_tva || 0}%`,
      eur(lht),
    ];
  });

  autoTable(pdf, {
    startY: Math.max(cy, my) + 8,
    head: [["Description", "Qté", "P.U. HT", "TVA", "Total HT"]],
    body,
    theme: "grid",
    headStyles: { fillColor: SLATE, textColor: 255, fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40], cellPadding: 3.5, lineHeight: 1.35, valign: "top", lineColor: [226, 232, 240] },
    columnStyles: {
      0: { cellWidth: 92 },
      1: { halign: "center", cellWidth: 16 },
      2: { halign: "right", cellWidth: 26 },
      3: { halign: "center", cellWidth: 18 },
      4: { halign: "right", cellWidth: 26 },
    },
    margin: { left: M, right: M },
  });

  // ---- Totals ---- (ensure room, else new page)
  const PAGE_H = 297;
  let ty = pdf.lastAutoTable.finalY + 8;
  if (ty > PAGE_H - 60) { pdf.addPage(); ty = 24; }
  const tx = 125;
  const totals = [
    ["Total HT", eur(doc.total_ht)],
    ["TVA", eur(doc.total_tva)],
  ];
  pdf.setFontSize(10);
  totals.forEach(([k, v]) => {
    pdf.setTextColor(...MUTED);
    pdf.setFont("helvetica", "normal");
    pdf.text(k, tx, ty);
    pdf.setTextColor(...SLATE);
    pdf.text(v, W - M, ty, { align: "right" });
    ty += 6;
  });
  pdf.setFillColor(...SLATE);
  pdf.rect(tx - 4, ty - 4, W - M - (tx - 4), 10, "F");
  pdf.setTextColor(...GOLD);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Total TTC", tx, ty + 2.5);
  pdf.setTextColor(255, 255, 255);
  pdf.text(eur(doc.total_ttc), W - M, ty + 2.5, { align: "right" });

  // ---- Footer computed first (so we know its top edge) ----
  const footerText = `${settings.company_name} · ${settings.address}, ${settings.postal_city} · SIRET ${settings.siret} · APE ${settings.ape || ""} · TVA ${settings.tva_number}`;
  const footerLines = pdf.splitTextToSize(footerText, W - 2 * M);
  const legalLines = settings.legal_footer ? pdf.splitTextToSize(settings.legal_footer, W - 2 * M) : [];
  const FLINE = 3.4;
  const footerBlockH = (footerLines.length + legalLines.length) * FLINE;
  const footerTop = PAGE_H - 6 - footerBlockH;

  // ---- Payment / notes (guard against footer collision) ----
  let py = ty + 16;
  const ensure = (needed) => {
    if (py + needed > footerTop - 4) { pdf.addPage(); py = 24; }
  };
  if (isInvoice && (settings.iban || settings.bic || settings.payment_terms)) {
    ensure(24);
    pdf.setFontSize(9);
    pdf.setTextColor(...SLATE);
    pdf.setFont("helvetica", "bold");
    pdf.text("Règlement", M, py);
    py += 5;
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...MUTED);
    if (settings.iban) { pdf.text(`IBAN : ${settings.iban}`, M, py); py += 5; }
    if (settings.bic) { pdf.text(`BIC : ${settings.bic}`, M, py); py += 5; }
    if (settings.payment_terms) {
      const t = pdf.splitTextToSize(settings.payment_terms, W - 2 * M);
      pdf.text(t, M, py);
      py += t.length * 4 + 3;
    }
  }
  if (doc.notes) {
    ensure(18);
    pdf.setFontSize(9);
    pdf.setTextColor(...SLATE);
    pdf.setFont("helvetica", "bold");
    pdf.text("Notes", M, py);
    py += 5;
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...MUTED);
    const n = pdf.splitTextToSize(doc.notes, W - 2 * M);
    pdf.text(n, M, py);
    py += n.length * 4;
  }

  // ---- Footer pinned to bottom of the last page ----
  pdf.setDrawColor(...GOLD);
  pdf.setLineWidth(0.5);
  pdf.line(M, footerTop - 4, W - M, footerTop - 4);
  pdf.setFontSize(7);
  pdf.setTextColor(...MUTED);
  let fy = footerTop;
  footerLines.forEach((l) => { pdf.text(l, W / 2, fy, { align: "center" }); fy += FLINE; });
  legalLines.forEach((l) => { pdf.text(l, W / 2, fy, { align: "center" }); fy += FLINE; });

  return pdf;
}

export function generateDocumentPDF(doc, settings, type = "invoice") {
  const pdf = buildDocumentPDF(doc, settings, type);
  const title = type === "invoice" ? "FACTURE" : "DEVIS";
  pdf.save(`${doc.number || title}.pdf`);
}

export function generateTransportOrderPDF(o, settings = {}) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 16, CW = W - 2 * M;
  const ex = o.exploitant || {};

  // Header band
  pdf.setFillColor(...SLATE);
  pdf.rect(0, 0, W, 32, "F");
  pdf.setFillColor(...GOLD);
  pdf.rect(0, 32, W, 1.5, "F");
  pdf.setTextColor(...GOLD);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.text(ex.raison_sociale || settings.company_name || "MB TRANSPORT", M, 15);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  if (ex.nom_commercial) pdf.text(ex.nom_commercial, M, 20);
  pdf.text("Transport de personnes — VTC", M, ex.nom_commercial ? 25 : 21);
  pdf.setTextColor(...GOLD);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text("BON DE TRANSPORT VTC", W - M, 15, { align: "right" });
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(`N° ${o.number || ""}`, W - M, 22, { align: "right" });

  let y = 40;
  pdf.setTextColor(...SLATE);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  pdf.text("JUSTIFICATIF DE LA RÉSERVATION PRÉALABLE", M, y);
  y += 4.3;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7.3);
  pdf.setTextColor(...MUTED);
  pdf.text("Article R. 3120-2 du Code des transports – Arrêté du 6 août 2025", M, y);
  y += 3.8;
  pdf.text("Prix affiché en TTC et HT (TVA 10%).", M, y);
  y += 7;
  const ensure = (h) => { if (y + h > 288) { pdf.addPage(); y = 22; } };
  const fdt = (v) => (v ? String(v).replace("T", " à ") : "—");

  const section = (title, pairs) => {
    const rows = pairs.filter((p) => p);
    ensure(12 + rows.length * 6);
    pdf.setFillColor(...SLATE);
    pdf.rect(M, y, CW, 7, "F");
    pdf.setTextColor(...GOLD);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.text(title, M + 3, y + 4.8);
    y += 10;
    pdf.setFontSize(9);
    rows.forEach(([label, value]) => {
      const val = value === 0 || value ? String(value) : "—";
      pdf.setTextColor(...MUTED);
      pdf.setFont("helvetica", "normal");
      pdf.text(label, M + 1, y);
      pdf.setTextColor(...SLATE);
      pdf.setFont("helvetica", "bold");
      const lines = pdf.splitTextToSize(val, CW - 56);
      pdf.text(lines, M + 55, y);
      y += Math.max(5, lines.length * 4.4) + 1.6;
    });
    y += 4;
  };

  let tarif;
  if (o.tarif_mention) {
    tarif = o.tarif_mention;
  } else if (o.tarif) {
    const ttcv = Number(o.tarif);
    const htv = ttcv / 1.1;
    tarif = `${eur(ttcv)} TTC  —  HT ${eur(htv)} + TVA 10% ${eur(ttcv - htv)}`;
  } else {
    tarif = "—";
  }

  section("Exploitant VTC", [
    ["Raison sociale", ex.raison_sociale],
    ex.nom_commercial ? ["Nom commercial", ex.nom_commercial] : null,
    ["Adresse", `${ex.address || ""} ${ex.postal_city || ""}`.trim()],
    ["SIREN", ex.siren],
    ["N° REVTC", ex.revtc],
    ["Téléphone", ex.phone],
  ]);

  section("Véhicule", [
    ["Marque et modèle", o.vehicle_marque_modele],
    ["Immatriculation", o.vehicle_immatriculation],
    o.vehicle_carte_pro ? ["N° carte pro chauffeur", o.vehicle_carte_pro] : null,
  ]);

  section("Client", [
    ["Nom / entreprise", o.client_name],
    ["Téléphone / e-mail", [o.client_phone, o.client_email].filter(Boolean).join("  ·  ")],
    ["Nombre de passagers", o.nb_passengers],
  ]);

  section("Course", [
    ["N° du bon", o.number],
    ["Date de réservation", frDate(o.date_reservation)],
    ["Prise en charge", `${frDate(o.date_prise_en_charge)}${o.heure_prise_en_charge ? " à " + o.heure_prise_en_charge : ""}`],
    ["Adresse de prise en charge", o.adresse_prise_en_charge],
    ["Destination", o.destination],
    o.arrets ? ["Arrêts intermédiaires", o.arrets] : null,
    ["Nombre de passagers", o.nb_passengers],
    ["Tarif convenu", tarif],
  ]);

  section("Chauffeur", [
    ["Nom et prénom", o.chauffeur_nom],
    ["N° carte professionnelle VTC", o.chauffeur_carte_pro],
  ]);

  section("Réservation", [
    ["Réservé par", o.reservation_par],
    ["Date et heure de réservation", fdt(o.reservation_datetime)],
    ["Moyen de réservation", o.reservation_moyen],
  ]);

  if (o.notes) section("Notes", [["", o.notes]]);

  // footer
  pdf.setDrawColor(...GOLD);
  pdf.setLineWidth(0.5);
  pdf.line(M, 289, W - M, 289);
  pdf.setFontSize(7);
  pdf.setTextColor(...MUTED);
  const foot = `${ex.raison_sociale || ""} · ${ex.address || ""}, ${ex.postal_city || ""} · SIREN ${ex.siren || ""} · REVTC ${ex.revtc || ""}`;
  pdf.text(pdf.splitTextToSize(foot, CW), W / 2, 293, { align: "center" });

  pdf.save(`${o.number || "BON-TRANSPORT"}.pdf`);
}
